import { useState, useEffect, useReducer } from "react";
import { Controller, useForm } from "react-hook-form";
import IdNameModel from "../../../models/common/IdNameModel";
import { GuidEnum } from "../../../models/enums/GuidEnum";
import toasterUtils from "../../../utils/toaster-utils";
import JobPostingSkillRequestModel from "../../../models/company/jobPosting/request/JobPostingSkillRequestModel";
import JobPostingInterviewProcessRequestModel from "../../../models/company/jobPosting/request/JobPostingInterviewProcessRequestModel";
import { FORM_SELECT_RULE } from "../../../utils/validation-utils";
import { Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormHelperText, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";

interface JobPostingInterviewProcessProps {
    objJobPostingInterviewProcess: JobPostingInterviewProcessRequestModel;
    actionType: string;
    secondarySkillList: IdNameModel[];
    interviewRoundList: IdNameModel[];
    onCloseJobPosting: () => void;
    onJobPostingInterviewProcessSave: (objJobPosting: JobPostingInterviewProcessRequestModel) => void;
};

const JobPostingInterviewProcessCreateComponent = (props: JobPostingInterviewProcessProps) => {
    const [formData, setFormData] = useState<JobPostingInterviewProcessRequestModel>(props.objJobPostingInterviewProcess);
    const [secondarySkillList, setSecondarySkillList] = useState<JobPostingSkillRequestModel[]>(props.objJobPostingInterviewProcess.jobPostingSkillRequestModel);

    useEffect(() => {
        setFormData(props.objJobPostingInterviewProcess);
    }, []);

    const {
        register,
        control,
        handleSubmit,
        getValues,
        unregister,
        formState: { errors, isValid },
    } = useForm<JobPostingInterviewProcessRequestModel>({
        mode: "onChange",
        shouldUnregister: true,
        defaultValues: formData
    });

    const onSubmit = (formData: JobPostingInterviewProcessRequestModel) => {
        if (!isValid) {
            toasterUtils.showError("Please fill all mandatory fields");
            return;
        }
        const skills = (formData.jobPostingSkillRequestModel ?? []).map(s => s.secondarySkillId).filter((x) => x !== null && x !== 0);
        const hasDuplicateSecondarySkill = skills.some((id, i) => skills.indexOf(id) !== i);
        if (hasDuplicateSecondarySkill) {
            toasterUtils.showError("Please select different secondary skills.");
            return;
        }
        formData.requirementInterviewProcessId = props.objJobPostingInterviewProcess.requirementInterviewProcessId;
        formData.jobPostingSkillRequestModel.forEach(function (item, index) {
            item.requirementSkillId = GuidEnum.Empty;
            item.requirementId = props.objJobPostingInterviewProcess.requirementId;
            item.secondarySkillId = Number(item.secondarySkillId);
            item.secondarySkillNumber = index + 1;
            item.requirementInterviewProcessId = GuidEnum.Empty;
            item.recruiterId = GuidEnum.Empty;
            item.userId = GuidEnum.Empty;
        });
        formData.userId = GuidEnum.Empty;
        props.onJobPostingInterviewProcessSave(formData);
    }

    const addNewSkill = () => {
        let skill: JobPostingSkillRequestModel = {
            requirementSkillId: GuidEnum.Empty,
            requirementId: props.objJobPostingInterviewProcess.requirementId,
            secondarySkillId: null,
            secondarySkillNumber: secondarySkillList.length + 1,
            isCommentsRequired: false,
            requirementInterviewProcessId: GuidEnum.Empty,
            recruiterId: GuidEnum.Empty,
            userId: GuidEnum.Empty
        };
        const updatedList = [...secondarySkillList]; // simply create a new array and spread the previous one
        updatedList.push(skill);
        setSecondarySkillList(updatedList);
        const updatedFormData = { ...formData };
        updatedFormData.jobPostingSkillRequestModel = updatedList;
        setFormData(updatedFormData);
    };

    const removeSkill = (index: number) => {
        const updatedList = formData.jobPostingSkillRequestModel.filter((_, i) => i !== index);
        setSecondarySkillList(updatedList);
        const updatedFormData = { ...formData, jobPostingSkillRequestModel: updatedList };
        setFormData(updatedFormData);
        unregister(`jobPostingSkillRequestModel.${index}`);
    }

    return (
        <div className="card card-white">
            <div className="card-body">
                <form className="contact quform" onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="interviewRoundId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.interviewRoundList.find(x => x.id === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.interviewRoundList} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Interview Round" size="small" required margin="normal" fullWidth error={!!errors.interviewRoundId} helperText={errors.interviewRoundId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {formData.jobPostingSkillRequestModel.map((skill, index) => (
                        <div className="row">
                            <div className="col-md-1">
                                <IconButton color="error" size="small" onClick={() => removeSkill(index)} disabled={index < 3} className="remove-skill-icon">
                                    <RemoveIcon />
                                </IconButton>
                            </div>
                            <div className="col-md-7">
                                <Controller name={`jobPostingSkillRequestModel.${index}.secondarySkillId`} control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                    render={({ field }) => (
                                        <Autocomplete {...field} value={props.secondarySkillList.find(x => x.id === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.secondarySkillList} getOptionLabel={(option) => option.name || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label={`Secondary Skill ${index + 1}`} size="small" required margin="normal" fullWidth error={!!errors.jobPostingSkillRequestModel?.[index]?.secondarySkillId} helperText={errors.jobPostingSkillRequestModel?.[index]?.secondarySkillId?.message} />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-md-4">
                                <Controller name={`jobPostingSkillRequestModel.${index}.isCommentsRequired`} control={control}
                                    render={({ field }) => (
                                        <FormControl margin="normal" error={!!errors?.jobPostingSkillRequestModel?.[index]?.isCommentsRequired}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox {...field} checked={field.value || false} disabled={(index + 1) < 4} />
                                                }
                                                label="Need Comments"
                                            />
                                            <FormHelperText>
                                                {errors?.jobPostingSkillRequestModel?.[index]?.isCommentsRequired?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                    <div className="row">
                        <div className="col-md-6"></div>
                        <div className="col-md-6">
                            <Button variant="outlined" color="primary" type="submit" onClick={addNewSkill}>
                                + Add New Skill
                            </Button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6"></div>
                        <div className="col-md-6">
                            <Button variant="contained" color="success" type="submit" disabled={!isValid}>
                                Save
                            </Button>
                            <Button variant="contained" color="error" className="m-l-xxs" type="button" onClick={props.onCloseJobPosting}>
                                Close
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default JobPostingInterviewProcessCreateComponent;